import {
  PortfolioAssetGroupsQuery,
  SavingsPlansViewQuery,
  StakingTransactionsViewQuery,
  TransactionStatus,
} from '@bts-web/data-layer/server';
import { PrecisionFloat } from '@bts-web/utils-formatting';
import { OrderForSummary } from '../../orders/types';

export type { Mutable } from '../../common/types/mutable';

export { AssetType } from '../../asset-details/types';

export type PoweredByTextType = {
  value?: string;
  fontSize?: string;
  centered?: boolean;
  noMargins?: boolean;
};

export type AssetNode = {
  __typename: string;
  id: string;
  logoUrl: string | null;
  name: string | null;
  symbol: string | null;
} | null;

export type NonNullableTransactionData = {
  __typename: string;
  asset?: AssetNode;
  status?: TransactionStatus;
  assetAmount: PrecisionFloat;
  fiatAmount: PrecisionFloat;
  totalAmount?: PrecisionFloat;
  taxAmount: PrecisionFloat;
  sourceAmount?: PrecisionFloat;
  sourceAsset?: AssetNode;
};

export type NonNullableStakeTransactionData = NonNullable<
  NonNullable<
    NonNullable<
      NonNullable<
        NonNullable<
          StakingTransactionsViewQuery['response']
        >['stakingTransactions']
      >['edges']
    >[number]
  >['node']
>;

export type NonNullableSavingsPlanData = NonNullable<
  NonNullable<
    NonNullable<
      NonNullable<
        NonNullable<SavingsPlansViewQuery['response']>['savingsPlans']
      >['edges']
    >[number]
  >['node']
>;

export type NonNullableOrderData = NonNullable<OrderForSummary>;

export type FinishTradeDataType =
  | NonNullableTransactionData
  | NonNullableSavingsPlanData
  | NonNullableStakeTransactionData
  | NonNullableOrderData;

/** Type-safe status extraction from finish-trade data (all union members may have optional status). */
export function getFinishTradeStatus(
  data: FinishTradeDataType,
): string | undefined {
  return 'status' in data && data.status != null
    ? String(data.status)
    : undefined;
}

/**
 * Constant mapping of `TradeControllerVariants` values.
 *
 * Use these instead of raw strings for type safety and readability.
 */
export const TradeOperation = {
  buy: 'buy',
  sell: 'sell',
  savings: 'savings',
  swap: 'swap',
  stake: 'stake',
  unstake: 'unstake',
  order: 'order',
} as const;

/**
 * Trade controller variants.
 *
 * ℹ️ Prefer using the `TradeOperation` constants instead of raw strings,
 * e.g. `VARIANT.buy` instead of `'buy'`.
 */
export type TradeControllerVariants =
  (typeof TradeOperation)[keyof typeof TradeOperation];

export type TradeTypesWithSwap = Exclude<TradeControllerVariants, 'savings'>;

export type TradeBuyApplicationStatePushTan = 'declined' | 'failed';

export type TradeFinishTransactionControllerVariants =
  | 'success'
  | 'failure'
  | 'pending';

export enum SwapAssetFilterTypes {
  ALL = 'all',
  CASH = 'cash',
}

export type PortfolioAssetGroupsEdges = NonNullable<
  NonNullable<PortfolioAssetGroupsQuery['response']['portfolio']>['assetGroups']
>['edges'];

export type AssetEdge =
  | NonNullable<
      NonNullable<
        NonNullable<
          NonNullable<PortfolioAssetGroupsEdges>[number]['node']
        >[number]
      >['edges']
    >[number]
  | undefined;
